import { Reveal } from "@components/ui";

interface FeeCardProps {
  title: string;
  price: string;
  description: string;
  index: number;
  icon: React.ReactNode;
}

function FeeCard({ title, price, description, index, icon }: FeeCardProps) {
  const watermark = String(index + 1).padStart(2, "0");

  return (
    <div className="group relative rounded-[16px] md:rounded-[20px] bg-white border border-transparent hover:border-[#e8e9ed] overflow-hidden flex flex-col items-start p-4 md:p-6 gap-4 md:gap-5 hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      {/* Watermark */}
      <span className="absolute top-3 right-4 md:top-4 md:right-5 text-[48px] md:text-[64px] font-bold text-[#122d63]/[0.04] leading-none font-albert-sans select-none pointer-events-none">
        {watermark}
      </span>

      {/* Icon */}
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#f24d2b]/8 text-[#f24d2b] flex items-center justify-center group-hover:bg-[#f24d2b] group-hover:text-white transition-colors duration-300">
        {icon}
      </div>

      {/* Title */}
      <span className="text-[13px] md:text-[15px] tracking-[-0.04em] text-[#62636c] font-albert-sans">
        {title}
      </span>

      {/* Price */}
      <h3 className="text-[22px] md:text-[28px] lg:text-[32px] tracking-[-0.04em] font-bold text-[#122d63] font-albert-sans leading-[1.1]">
        {price}
      </h3>

      {/* Description */}
      <p className="text-[13px] md:text-[15px] lg:text-[17px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.5]">
        {description}
      </p>
    </div>
  );
}

const fees = [
  {
    title: "Security Deposit",
    price: "500 - 1500 AED",
    description: "Refundable deposit based on unit type",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Connection/Installation fee",
    price: "800 - 2000 AED",
    description: "One-time charge based on meter size & pipe length",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94L6.73 20.2a2 2 0 01-2.83 0l-.1-.1a2 2 0 010-2.83l6.73-6.73A6 6 0 0118.47 2.53L14.7 6.3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Inspection and testing fee",
    price: "250 - 400 AED",
    description: "Includes safety inspection & certification",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Annual Maintenance",
    price: "200 - 500 AED",
    description: "Regular safety checks & support",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function TransparentFeesSection() {
  return (
    <section className="w-full bg-[#f9f9fb] overflow-hidden pt-8 lg:pt-[60px] pb-10 md:pb-14 lg:pb-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 md:gap-14 lg:gap-16">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Pricing
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Transparent Fees — No Hidden Charges
            </h2>
            <p className="text-[14px] md:text-base lg:text-[22px] tracking-[-0.04em] text-[#62636c] max-w-[800px]">
              All fees include 5% VAT. Deposits are fully refundable upon
              account closure and final bill settlement.
            </p>
          </div>
        </Reveal>

        {/* Fee Cards */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {fees.map((fee, index) => (
            <Reveal key={fee.title} direction="up" delay={index * 100}>
              <FeeCard {...fee} index={index} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
